import { useEffect, useId, useMemo, useState } from "react";
import { Maximize2, Minus, Plus, RotateCcw } from "lucide-react";

import { cn } from "@monorepo-apps/ui/src/lib/utils";

import { MERMAID_INIT_CONFIG } from "@/presentation/components/diagrams/mermaid-theme";

interface MermaidApi {
  initialize: (config: typeof MERMAID_INIT_CONFIG) => void;
  render: (id: string, chart: string) => Promise<{ svg: string }>;
}

let mermaidModulePromise: Promise<MermaidApi> | null = null;
let initialized = false;

async function loadMermaid(): Promise<MermaidApi> {
  if (!mermaidModulePromise) {
    mermaidModulePromise = import("mermaid").then((mod) => {
      const api = (mod.default ?? mod) as unknown as MermaidApi;
      if (!initialized) {
        api.initialize(MERMAID_INIT_CONFIG);
        initialized = true;
      }
      return api;
    });
  }
  return mermaidModulePromise;
}

interface MermaidDiagramProps {
  chart: string;
  eyebrow: string;
  title: string;
  caption?: string | null;
  fallbackHeight: number;
}

type RenderState =
  | { phase: "loading" }
  | { phase: "ready"; svg: string }
  | { phase: "error"; message: string };

export function MermaidDiagram({
  chart,
  eyebrow,
  title,
  caption,
  fallbackHeight,
}: MermaidDiagramProps) {
  const reactId = useId();
  // Mermaid's render() id must be a CSS-id-safe string; useId yields ":r0:"
  // style values that Mermaid rejects. Sanitize and prefix.
  const renderId = useMemo(() => `mermaid-${reactId.replace(/[^a-zA-Z0-9_-]/g, "")}`, [reactId]);
  const [state, setState] = useState<RenderState>({ phase: "loading" });

  useEffect(() => {
    let cancelled = false;

    loadMermaid()
      .then(async (api) => {
        const result = await api.render(renderId, chart);
        if (!cancelled) {
          setState({ phase: "ready", svg: result.svg });
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setState({ phase: "error", message });
      });

    return () => {
      cancelled = true;
    };
  }, [chart, renderId]);

  if (state.phase === "loading") {
    return (
      <DiagramShell eyebrow={eyebrow} title={title} caption={caption}>
        <div
          data-slot="mermaid-skeleton"
          className="bg-muted/40 flex w-full items-center justify-center rounded-md"
          style={{ height: fallbackHeight }}
        >
          <span className="font-data text-muted-foreground text-[10px] tracking-[0.18em] uppercase">
            loading diagram…
          </span>
        </div>
      </DiagramShell>
    );
  }

  if (state.phase === "error") {
    return (
      <DiagramShell
        eyebrow="diagram render failed"
        title={title}
        caption={state.message}
        tone="error"
      >
        <pre className="bg-muted/40 text-foreground/80 max-h-[420px] overflow-auto rounded-md p-4 text-[11px] leading-relaxed">
          <code className="font-data">{chart}</code>
        </pre>
      </DiagramShell>
    );
  }

  return (
    <DiagramShell eyebrow={eyebrow} title={title} caption={caption}>
      <ZoomableSvg svg={state.svg} />
    </DiagramShell>
  );
}

// ─── Zoomable / scrollable SVG container ─────────────────────────────────
// Mermaid renders SVGs at natural size (useMaxWidth: false). For dense
// sequence diagrams that's still wider than the card, so we wrap in a
// scroll container with explicit zoom controls. Zoom uses CSS transform
// for smoothness; transform-origin top-left keeps the upper-left corner
// anchored so scroll position stays predictable.

const ZOOM_MIN = 0.4;
const ZOOM_MAX = 2.5;
const ZOOM_STEP = 0.2;
const ZOOM_DEFAULT = 0.6;

function ZoomableSvg({ svg }: { svg: string }) {
  const [zoom, setZoom] = useState(ZOOM_DEFAULT);

  function zoomIn() {
    setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)));
  }
  function zoomOut() {
    setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)));
  }
  function reset() {
    setZoom(ZOOM_DEFAULT);
  }

  return (
    <div data-slot="zoomable-svg-wrapper" className="relative">
      <ZoomControls zoom={zoom} onIn={zoomIn} onOut={zoomOut} onReset={reset} />
      <div
        data-slot="mermaid-svg-scroll"
        className="bg-grid-fine max-h-[640px] w-full overflow-auto rounded-md border border-[var(--border)]"
      >
        <div
          data-slot="mermaid-svg"
          style={{
            transform: `scale(${zoom})`,
            transformOrigin: "top left",
            // SVG natural width can exceed container; inline-block lets it size
            // to content so the scroll container reports the true width.
            display: "inline-block",
            padding: 16,
          }}
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      </div>
    </div>
  );
}

function ZoomControls({
  zoom,
  onIn,
  onOut,
  onReset,
}: {
  zoom: number;
  onIn: () => void;
  onOut: () => void;
  onReset: () => void;
}) {
  return (
    <div
      data-slot="zoom-controls"
      className="absolute top-2 right-2 z-10 flex items-center gap-0.5 rounded-md border border-[var(--border)] bg-white/90 p-0.5 shadow-sm backdrop-blur"
    >
      <ZoomButton onClick={onOut} disabled={zoom <= ZOOM_MIN} title="Zoom out">
        <Minus className="size-3.5" />
      </ZoomButton>
      <span className="font-data text-muted-foreground min-w-[40px] px-1 text-center text-[10px] tabular-nums">
        {Math.round(zoom * 100)}%
      </span>
      <ZoomButton onClick={onIn} disabled={zoom >= ZOOM_MAX} title="Zoom in">
        <Plus className="size-3.5" />
      </ZoomButton>
      <span className="bg-border mx-0.5 h-4 w-px" />
      <ZoomButton onClick={onReset} disabled={zoom === ZOOM_DEFAULT} title="Reset zoom">
        <RotateCcw className="size-3.5" />
      </ZoomButton>
      <ZoomButton
        onClick={() => {
          const el = document.querySelector(
            '[data-slot="mermaid-svg-scroll"]'
          ) as HTMLElement | null;
          if (!el) return;
          if (document.fullscreenElement) document.exitFullscreen();
          else el.requestFullscreen?.();
        }}
        title="Fullscreen"
      >
        <Maximize2 className="size-3.5" />
      </ZoomButton>
    </div>
  );
}

function ZoomButton({
  children,
  onClick,
  disabled,
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  title: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="text-foreground/65 hover:bg-foreground/5 hover:text-foreground inline-flex size-7 items-center justify-center rounded transition-colors disabled:cursor-not-allowed disabled:opacity-30"
    >
      {children}
    </button>
  );
}

interface DiagramShellProps {
  eyebrow: string;
  title: string;
  caption?: string | null;
  tone?: "default" | "error";
  children: React.ReactNode;
}

function DiagramShell({ eyebrow, title, caption, tone = "default", children }: DiagramShellProps) {
  return (
    <section
      data-slot="mermaid-diagram"
      className={cn("glow-card flex flex-col gap-4 p-6", tone === "error" && "ring-1")}
    >
      <header className="flex flex-col gap-1.5 border-b border-[var(--border)] pb-4">
        <span className="eyebrow">{eyebrow}</span>
        <h2 className="text-[18px] font-semibold tracking-tight">{title}</h2>
        {caption ? (
          <p className="text-muted-foreground text-[13px] leading-relaxed">{caption}</p>
        ) : null}
      </header>
      {children}
    </section>
  );
}
